'use client'

import { useEffect, useRef } from 'react'

import { toast } from '@/core/lib/toast'
import { isCheckoutInterrupted } from '@/features/veluvit/lib/payment-status'
import { usePaymentOrder } from '@/features/veluvit/hooks/use-payment-order'
import {
  clearPurchaseAttempt,
  readPurchaseAttempt,
} from '@/features/veluvit/services/payment.service'
import { usePaymentFlowStore } from '@/features/veluvit/stores/payment-flow.store'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type { WatchedPayment } from '@/features/veluvit/stores/payment-flow.store'

const announced = new Set<string>()
function announce(key: string) {
  if (announced.has(key)) return false
  try {
    if (sessionStorage.getItem(key)) return false
    sessionStorage.setItem(key, '1')
  } catch {
    /* 메모리 중복 방지를 유지한다. */
  }
  announced.add(key)
  return true
}

function PaymentMonitor({ payment }: { payment: WatchedPayment }) {
  const { order, error, walletError, walletSettled, check, refreshWallet } =
    usePaymentOrder(payment.ownerId, payment.orderId, { autoSync: false })
  const walletToast = useRef<string | null>(null)
  const reconciled = useRef(false)
  useEffect(() => {
    if (
      reconciled.current ||
      order?.status !== 'pending' ||
      order.payment.phase !== 'confirming'
    )
      return
    const timer = setTimeout(() => {
      reconciled.current = true
      void check()
    }, 8000)
    return () => clearTimeout(timer)
  }, [check, order?.status, order?.payment.phase])
  useEffect(() => {
    const session = useSessionStore.getState()
    if (
      !order ||
      session.identity !== payment.identity ||
      session.data?.user?.id !== payment.ownerId
    )
      return
    const key = `veluvit:payment:notice:${payment.ownerId}:${order.id}:${order.status}`
    if (order.status === 'paid') {
      if (!walletSettled || walletError) return
      usePaymentFlowStore
        .getState()
        .complete(payment.ownerId, order.id, payment.layerId)
      if (announce(key)) {
        toast.success('젬 충전이 완료됐어요.')
        const product = order.product
        const amount =
          (product.credits ?? 0) +
          (product.bonus ?? 0) +
          (product.firstCharge?.bonus ?? 0)
        usePaymentFlowStore
          .getState()
          .showGain(payment.ownerId, order.id, amount)
      }
      try {
        if (readPurchaseAttempt(payment.ownerId)?.orderId === order.id)
          clearPurchaseAttempt(payment.ownerId)
      } catch {
        /* 서버 확정 결과와 알림은 보관소 오류에 영향받지 않는다. */
      }
    } else if (order.status === 'awaiting_deposit') {
      if (announce(key))
        toast.show('입금 계좌가 발급됐어요. 입금이 확인되면 젬이 지급돼요.')
    } else if (isCheckoutInterrupted(order)) {
      if (announce(key + ':interrupted'))
        toast.show(order.payment.resultMessage || '결제를 취소했어요.')
    } else if (order.status === 'failed') {
      if (announce(key))
        toast.error(order.payment.resultMessage || '결제가 완료되지 않았어요.')
    }
  }, [order, payment, walletError, walletSettled])
  useEffect(() => {
    if (!walletError) {
      if (walletToast.current) toast.dismiss(walletToast.current)
      walletToast.current = null
      return
    }
    const session = useSessionStore.getState()
    if (
      session.identity !== payment.identity ||
      session.data?.user?.id !== payment.ownerId ||
      walletToast.current
    )
      return
    walletToast.current = toast.show(
      '결제는 완료됐어요. 젬 내역을 다시 확인해 주세요.',
      {
        duration: 0,
        actions: [
          {
            id: 'wallet',
            label: '다시 확인',
            onClick: () => {
              void refreshWallet()
            },
          },
        ],
      }
    )
  }, [payment.identity, payment.ownerId, refreshWallet, walletError])
  useEffect(
    () => () => {
      if (walletToast.current) toast.dismiss(walletToast.current)
    },
    []
  )
  useEffect(() => {
    const session = useSessionStore.getState()
    if (
      !error ||
      order ||
      session.identity !== payment.identity ||
      session.data?.user?.id !== payment.ownerId
    )
      return
    const key = `veluvit:payment:notice:${payment.ownerId}:${payment.orderId}:lookup`
    if (announce(key))
      toast.show('주문 상태를 확인하지 못했어요. 다시 확인해 주세요.', {
        actions: [
          {
            id: 'check',
            label: '다시 확인',
            onClick: () => {
              void check()
            },
          },
        ],
      })
  }, [check, error, order, payment])
  useEffect(() => {
    if (
      (order && isCheckoutInterrupted(order)) ||
      order?.status === 'failed' ||
      (walletSettled &&
        !walletError &&
        order &&
        ['paid', 'partially_refunded', 'refunded'].includes(order.status))
    ) {
      usePaymentFlowStore.getState().remove(payment.ownerId, payment.orderId)
    }
  }, [order, payment.orderId, payment.ownerId, walletError, walletSettled])
  return null
}

/** 결제창을 닫거나 시트에서 뒤로 가도 서버 결과와 지갑 갱신을 끝낸다. */
export default function PaymentFlowHost() {
  const payments = usePaymentFlowStore((state) => state.payments)
  const gain = usePaymentFlowStore((state) => state.gain)
  const completed = usePaymentFlowStore((state) => state.completed)
  const identity = useSessionStore((state) => state.identity)
  const ownerId = useSessionStore((state) =>
    state.data?.user?.guest ? undefined : state.data?.user?.id
  )
  useEffect(() => {
    for (const item of payments) {
      if (item.identity !== identity || item.ownerId !== ownerId)
        usePaymentFlowStore.getState().remove(item.ownerId, item.orderId)
    }
  }, [identity, ownerId, payments])
  useEffect(() => {
    if (!gain) return
    const timer = setTimeout(
      () => usePaymentFlowStore.getState().clearGain(gain.orderId),
      1750
    )
    return () => clearTimeout(timer)
  }, [gain])
  useEffect(() => {
    if (!completed) return
    const timer = setTimeout(
      () =>
        usePaymentFlowStore
          .getState()
          .clearCompleted(completed.ownerId, completed.orderId),
      1800
    )
    return () => clearTimeout(timer)
  }, [completed])
  return (
    <>
      {payments
        .filter(
          (item) => item.identity === identity && item.ownerId === ownerId
        )
        .map((item) => (
          <PaymentMonitor
            key={item.ownerId + ':' + item.orderId}
            payment={item}
          />
        ))}
    </>
  )
}
